import type { ReactNode } from "react"

import { cx } from "../lib/cx"
import styles from "./status-stripe.module.css"

/**
 * Полоса состояния — одна штука.
 *
 * ЧТО ЭТО ТАКОЕ. Полоса говорит о состоянии СЕЙЧАС: нет связи, работаем от чужого
 * имени, оплата просрочена. Центр уведомлений говорит о том, что ПРОИЗОШЛО. Одно и то же
 * не сообщается дважды, поэтому «нет связи» — это полоса и только полоса.
 *
 * ПОЧЕМУ ОНА ЗДЕСЬ, А НЕ ТОЛЬКО В ОБОЛОЧКЕ. Оболочка решает, КАКИЕ полосы показать
 * и в каком порядке (shell/build-stripes.ts и shell/service-stripes.tsx — правило
 * «сверху вниз: нет связи, режим поддержки, оплата»). Здесь — как выглядит ОДНА полоса
 * и как она ведёт себя на 360, где макет мастера снимает с неё фиксированную высоту
 * и разрешает тексту переноситься. Это разные вопросы, и ответы у них разные.
 *
 * ЗАМЕЧАНИЕ К СБОРКЕ: shell/service-stripes.tsx сейчас рисует полосу своей разметкой.
 * Файл чужой, я его не трогал; при сведении веток он заменяется на вызов этого
 * компонента — и заодно получает перенос текста на телефоне, которого у него нет.
 */

export type StripeTone = "offline" | "impersonate" | "billing"

const TONE_STYLE: Readonly<Record<StripeTone, string | undefined>> = {
  offline: styles.offline,
  impersonate: styles.impersonate,
  billing: styles.billing,
}

export function StatusStripe(props: {
  readonly tone: StripeTone
  /** «Нет связи», «Режим поддержки», «Оплата просрочена». */
  readonly title: string
  readonly text: string
  readonly action?:
    | {
        readonly label: string
        readonly onClick: () => void
      }
    | undefined
  readonly className?: string | undefined
}): ReactNode {
  return (
    /*
     * Внешний узел нужен не для красоты: он объявлен контейнером, и по ЕГО ширине
     * полоса решает, переносить текст или обрезать (см. .host в модуле стилей).
     *
     * role="status" с вежливым объявлением: появление полосы «нет связи» надо сообщить,
     * но не перебивая человека посреди набора телефона клиента. role="alert" здесь
     * был бы грубее, чем требует случай: данные на экране остались, они просто устарели.
     */
    <div className={cx(styles.host, props.className)} role="status">
      <div className={cx(styles.stripe, TONE_STYLE[props.tone])}>
        <span className={styles.title}>{props.title}</span>
        <span className={styles.text}>{props.text}</span>
        {props.action === undefined ? null : (
          <button type="button" className={styles.action} onClick={props.action.onClick}>
            {props.action.label}
          </button>
        )}
      </div>
    </div>
  )
}
